(function () {
  class SizeGuidePage {
    constructor(root) {
      this.root = root;
      this.tabsShell = root.querySelector("[data-size-guide-tabs-shell]");
      this.tabsWrapper = root.querySelector("[data-size-guide-tabs-wrapper]");
      this.tabsTrack = root.querySelector("[data-size-guide-tabs-track]");
      this.tabsNavWrap = root.querySelector("[data-size-guide-tabs-nav-wrap]");
      this.content = root.querySelector("[data-size-guide-content]");
      this.toolUpsellContent = root.querySelector("[data-size-guide-tool-upsell-content]");
      this.upsellContent = root.querySelector("[data-size-guide-upsell-content]");
      this.upsellTitleTarget = root.querySelector("[data-size-guide-upsell-title-target]");
      this.upsellNav = root.querySelector("[data-size-guide-upsell-nav]");
      this.upsellPrevButton = root.querySelector("[data-size-guide-upsell-prev]");
      this.upsellNextButton = root.querySelector("[data-size-guide-upsell-next]");
      this.prevButton = root.querySelector("[data-size-guide-tabs-prev]");
      this.nextButton = root.querySelector("[data-size-guide-tabs-next]");
      this.tabs = [];
      this.panels = [];
      this.toolUpsellPanels = [];
      this.upsellPanels = [];
      this.activeCode = "";
      this.prefersReducedMotion = window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;
      this.tickingStickyShadow = false;

      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    init() {
      this.sortMetaobjectCategories();
      if (!this.tabs.length || !this.panels.length) return;

      this.bindTabs();
      this.bindMetaFaqAccordions();
      this.bindCarouselControls();
      this.bindUpsellControls();
      this.bindToolUpsellQuickAdd();
      this.activateInitialTab();
      this.measureLayout();
      window.addEventListener("resize", this.handleResize, { passive: true });
      window.addEventListener("scroll", this.handleScroll, { passive: true });
    }

    sortMetaobjectCategories() {
      if (!this.tabsTrack || !this.content) return;

      var tabCandidates = Array.from(this.root.querySelectorAll("[data-size-guide-tab]"));
      var panelCandidates = Array.from(this.root.querySelectorAll("[data-size-guide-panel]"));
      if (!tabCandidates.length || !panelCandidates.length) return;

      var pairs = tabCandidates
        .map((tab) => {
          var targetId = tab.getAttribute("data-size-guide-target") || tab.getAttribute("aria-controls");
          var panel = targetId ? this.root.querySelector("#" + CSS.escape(targetId)) : null;
          if (!panel) return null;

          var orderRaw = parseInt(tab.getAttribute("data-size-guide-order") || panel.getAttribute("data-size-guide-order") || "", 10);
          var order = Number.isFinite(orderRaw) ? orderRaw : Number.MAX_SAFE_INTEGER;
          var label = (tab.getAttribute("data-size-guide-label") || panel.getAttribute("data-size-guide-label") || "").trim().toLowerCase();

          return {
            tab: tab,
            panel: panel,
            order: order,
            label: label
          };
        })
        .filter(Boolean);

      if (!pairs.length) return;

      pairs.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.label.localeCompare(b.label);
      });

      var tabFragment = document.createDocumentFragment();
      var sectionFragment = document.createDocumentFragment();

      pairs.forEach((pair) => {
        tabFragment.appendChild(pair.tab);
        sectionFragment.appendChild(pair.panel);
      });

      this.tabsTrack.appendChild(tabFragment);
      this.content.appendChild(sectionFragment);
      this.tabs = pairs.map((pair) => pair.tab);
      this.panels = pairs.map((pair) => pair.panel);
      this.sortToolUpsellPanels(pairs);
      this.sortUpsellPanels(pairs);
    }

    sortToolUpsellPanels(pairs) {
      if (!this.toolUpsellContent) return;

      var toolUpsellCandidates = Array.from(this.root.querySelectorAll("[data-size-guide-tool-panel]"));
      if (!toolUpsellCandidates.length) return;

      var upsellByCode = {};
      toolUpsellCandidates.forEach((panel) => {
        var code = this.normalizeGuideCode(panel.getAttribute("data-size-guide-code"));
        if (!code) return;
        upsellByCode[code] = panel;
      });

      var upsellFragment = document.createDocumentFragment();
      var orderedUpsells = [];

      pairs.forEach((pair) => {
        var code = this.normalizeGuideCode(
          pair.tab.getAttribute("data-size-guide-code") || pair.panel.getAttribute("data-size-guide-code") || ""
        );
        if (!code) return;

        var upsellPanel = upsellByCode[code];
        if (!upsellPanel) return;

        upsellFragment.appendChild(upsellPanel);
        orderedUpsells.push(upsellPanel);
      });

      if (!orderedUpsells.length) return;
      this.toolUpsellContent.appendChild(upsellFragment);
      this.toolUpsellPanels = orderedUpsells;
    }

    sortUpsellPanels(pairs) {
      if (!this.upsellContent) return;

      var upsellCandidates = Array.from(this.root.querySelectorAll("[data-size-guide-upsell-panel]"));
      if (!upsellCandidates.length) return;

      var upsellByCode = {};
      upsellCandidates.forEach((panel) => {
        var code = this.normalizeGuideCode(panel.getAttribute("data-size-guide-code"));
        if (!code) return;
        upsellByCode[code] = panel;
      });

      var upsellFragment = document.createDocumentFragment();
      var orderedUpsells = [];

      pairs.forEach((pair) => {
        var code = this.normalizeGuideCode(
          pair.tab.getAttribute("data-size-guide-code") || pair.panel.getAttribute("data-size-guide-code") || ""
        );
        if (!code) return;

        var upsellPanel = upsellByCode[code];
        if (!upsellPanel) return;

        upsellFragment.appendChild(upsellPanel);
        orderedUpsells.push(upsellPanel);
      });

      if (!orderedUpsells.length) return;
      this.upsellContent.appendChild(upsellFragment);
      this.upsellPanels = orderedUpsells;
    }

    bindTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => {
          this.activateTab(tab, {
            setFocus: false,
            updateHistory: true,
            ensureVisible: true
          });
        });

        tab.addEventListener("keydown", (event) => {
          this.handleTabKeydown(event, index);
        });
      });
    }

    activateInitialTab() {
      var initialCode = this.getInitialGuideCode();
      var initialTab = this.getTabByCode(initialCode) || this.tabs[0];
      if (!initialTab) return;

      this.activateTab(initialTab, {
        setFocus: false,
        updateHistory: false,
        ensureVisible: false
      });
    }

    handleTabKeydown(event, index) {
      if (!this.tabs.length) return;

      var key = event.key;
      var nextIndex = index;
      var activate = false;

      if (key === "ArrowLeft" || key === "ArrowUp") {
        event.preventDefault();
        nextIndex = index > 0 ? index - 1 : this.tabs.length - 1;
        activate = true;
      } else if (key === "ArrowRight" || key === "ArrowDown") {
        event.preventDefault();
        nextIndex = index < this.tabs.length - 1 ? index + 1 : 0;
        activate = true;
      } else if (key === "Home") {
        event.preventDefault();
        nextIndex = 0;
        activate = true;
      } else if (key === "End") {
        event.preventDefault();
        nextIndex = this.tabs.length - 1;
        activate = true;
      } else if (key === "Enter" || key === " ") {
        event.preventDefault();
        activate = true;
      }

      if (activate) {
        this.activateTab(this.tabs[nextIndex], {
          setFocus: true,
          updateHistory: true,
          ensureVisible: true
        });
      }
    }

    bindCarouselControls() {
      if (!this.tabsWrapper) return;

      if (this.prevButton) {
        this.prevButton.addEventListener("click", () => this.switchTabByDirection(-1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", () => this.switchTabByDirection(1));
      }

      this.tabsWrapper.addEventListener(
        "scroll",
        () => {
          this.updateArrowState();
        },
        { passive: true }
      );
    }

    bindUpsellControls() {
      if (!this.upsellPrevButton && !this.upsellNextButton) return;

      if (this.upsellPrevButton) {
        this.upsellPrevButton.addEventListener("click", () => this.scrollUpsell(-1));
      }

      if (this.upsellNextButton) {
        this.upsellNextButton.addEventListener("click", () => this.scrollUpsell(1));
      }

      this.upsellPanels.forEach((panel) => {
        var carousel = panel.querySelector(".size-guide-page__upsell-carousel");
        if (!carousel) return;

        carousel.addEventListener(
          "scroll",
          () => {
            this.updateUpsellArrowState();
          },
          { passive: true }
        );
      });
    }

    bindToolUpsellQuickAdd() {
      if (!this.root.querySelector("[data-size-guide-tool-upsell-add]")) return;

      this.root.addEventListener("click", (event) => {
        var trigger = event.target.closest("[data-size-guide-tool-upsell-add]");
        if (!trigger || !this.root.contains(trigger)) return;

        event.preventDefault();
        this.addToolUpsellToCart(trigger);
      });
    }

    addToolUpsellToCart(trigger) {
      if (!trigger || trigger.disabled) return;

      var toolUpsell = trigger.closest("[data-size-guide-tool-upsell]");
      if (!toolUpsell) return;

      var variantId = parseInt(toolUpsell.getAttribute("data-size-guide-tool-variant-id") || "", 10);
      if (!Number.isFinite(variantId)) {
        $s.addToCartFail("This item is unavailable right now.");
        return;
      }

      var defaultLabel = trigger.getAttribute("data-default-label") || trigger.textContent || "Add to cart";
      var loadingLabel = trigger.getAttribute("data-loading-label") || "Adding...";
      trigger.disabled = true;
      trigger.textContent = loadingLabel;

      $s.showLoadingOverlay();

      var addUrl = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add.js";

      window
        .fetch(addUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }]
          })
        })
        .then((response) => {
          if (response.ok) return response.json();
          return response
            .json()
            .catch(() => ({}))
            .then((data) => {
              throw new Error((data && data.description) || "Unable to add this item to cart.");
            });
        })
        .then(() => {
          // P6-E3: pass source through so cart:updated listeners can
          // distinguish size-guide quick-add ATCs.
          $s.addToCartSuccess(undefined, 'size-guide-quick-add');
        })
        .catch((error) => {
          var message = error && error.message ? error.message : "Unable to add this item to cart.";
          $s.addToCartFail(message);
        })
        .finally(() => {
          trigger.disabled = false;
          trigger.textContent = defaultLabel;
        });
    }

    bindMetaFaqAccordions() {
      var faqItems = Array.from(this.root.querySelectorAll(".size-guide-page__meta-faq-item"));
      if (!faqItems.length) return;

      faqItems.forEach((item) => {
        var summary = item.querySelector("summary");
        var answer = item.querySelector(".size-guide-page__meta-faq-answer");
        if (!summary || !answer) return;

        item.addEventListener("toggle", () => {
          if (!item.open) return;
          if (item.dataset.metaFaqClosing === "true") return;
          answer.style.maxHeight = "";
          answer.style.padding = "";
          answer.style.opacity = "";
          answer.style.borderTopColor = "";
        });

        summary.addEventListener("click", (event) => {
          if (!item.open) {
            answer.style.maxHeight = "";
            answer.style.padding = "";
            answer.style.opacity = "";
            answer.style.borderTopColor = "";
            return;
          }

          event.preventDefault();
          if (item.dataset.metaFaqClosing === "true") return;

          item.dataset.metaFaqClosing = "true";

          answer.style.maxHeight = answer.scrollHeight + "px";
          answer.style.padding = window.matchMedia("(max-width: 749px)").matches ? "0.9em" : "1em";
          answer.style.opacity = "1";
          answer.style.borderTopColor = "var(--size-guide-faq-border)";

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              answer.style.maxHeight = "0px";
              answer.style.padding = "0";
              answer.style.opacity = "0";
              answer.style.borderTopColor = "transparent";
            });
          });

          var completed = false;
          var finishClose = () => {
            if (completed) return;
            completed = true;
            item.open = false;
            item.dataset.metaFaqClosing = "false";
            answer.style.maxHeight = "";
            answer.style.padding = "";
            answer.style.opacity = "";
            answer.style.borderTopColor = "";
          };

          var onTransitionEnd = (transitionEvent) => {
            if (transitionEvent.target !== answer) return;
            if (transitionEvent.propertyName !== "max-height") return;
            answer.removeEventListener("transitionend", onTransitionEnd);
            finishClose();
          };

          answer.addEventListener("transitionend", onTransitionEnd);
          window.setTimeout(() => {
            answer.removeEventListener("transitionend", onTransitionEnd);
            finishClose();
          }, 1200);
        });
      });
    }

    switchTabByDirection(direction) {
      if (!this.tabs.length) return;

      var activeIndex = this.tabs.findIndex((tab) => {
        return tab.getAttribute("aria-selected") === "true";
      });
      if (activeIndex < 0) activeIndex = 0;

      var nextIndex = activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.tabs.length) return;

      this.activateTab(this.tabs[nextIndex], {
        setFocus: false,
        updateHistory: true,
        ensureVisible: true
      });
    }

    ensureTabVisible(tab) {
      if (!this.tabsWrapper || !tab) return;

      var wrapperRect = this.tabsWrapper.getBoundingClientRect();
      var tabRect = tab.getBoundingClientRect();
      var currentScrollLeft = this.tabsWrapper.scrollLeft || 0;
      var centerOffset = wrapperRect.width / 2 - tabRect.width / 2;
      var nextScrollLeft = currentScrollLeft + (tabRect.left - wrapperRect.left) - centerOffset;
      var maxScrollLeft = Math.max(0, this.tabsWrapper.scrollWidth - this.tabsWrapper.clientWidth);

      nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextScrollLeft));

      this.tabsWrapper.scrollTo({
        left: nextScrollLeft,
        behavior: this.prefersReducedMotion ? "auto" : "smooth"
      });
    }

    scrollUpsell(direction) {
      var carousel = this.getActiveUpsellCarousel();
      if (!carousel) return;

      var slide = carousel.querySelector(".size-guide-page__upsell-slide");
      var step = slide ? slide.getBoundingClientRect().width : Math.max(220, carousel.clientWidth * 0.5);

      carousel.scrollBy({
        left: direction * step,
        behavior: this.prefersReducedMotion ? "auto" : "smooth"
      });
    }

    getTabsHeight() {
      if (!this.tabsShell) return 0;
      return this.tabsShell.getBoundingClientRect().height;
    }

    measureLayout() {
      this.updateArrowState();
      this.updateUpsellArrowState();
      this.root.style.setProperty("--size-guide-tabs-offset", this.getTabsHeight() + "px");
      this.updateStickyShadowState();
    }

    updateArrowState() {
      if (!this.tabsWrapper) return;

      var tabCount = this.tabs.length;
      var hasMultipleTabs = tabCount > 1;
      var activeIndex = this.tabs.findIndex((tab) => {
        return tab.getAttribute("aria-selected") === "true";
      });
      if (activeIndex < 0) activeIndex = 0;
      var isAtStart = activeIndex <= 0;
      var isAtEnd = activeIndex >= tabCount - 1;

      if (this.tabsNavWrap) {
        this.tabsNavWrap.classList.toggle("is-hidden", !hasMultipleTabs);
      }

      if (this.prevButton) {
        this.prevButton.classList.remove("is-hidden");
        this.prevButton.disabled = !hasMultipleTabs || isAtStart;
      }

      if (this.nextButton) {
        this.nextButton.classList.remove("is-hidden");
        this.nextButton.disabled = !hasMultipleTabs || isAtEnd;
      }
    }

    getActiveUpsellCarousel() {
      if (!this.upsellPanels.length) return null;

      var activePanel = this.upsellPanels.find((panel) => !panel.hasAttribute("hidden")) || this.upsellPanels[0];
      if (!activePanel) return null;

      return activePanel.querySelector(".size-guide-page__upsell-carousel");
    }

    updateUpsellArrowState() {
      if (!this.upsellPrevButton && !this.upsellNextButton) return;

      var carousel = this.getActiveUpsellCarousel();
      if (!carousel) {
        if (this.upsellPrevButton) this.upsellPrevButton.disabled = true;
        if (this.upsellNextButton) this.upsellNextButton.disabled = true;
        if (this.upsellNav) this.upsellNav.classList.add("is-hidden");
        return;
      }

      var maxScrollLeft = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
      var hasOverflow = maxScrollLeft > 2;
      var currentScrollLeft = carousel.scrollLeft || 0;
      var isAtStart = currentScrollLeft <= 2;
      var isAtEnd = currentScrollLeft >= maxScrollLeft - 2;

      if (this.upsellPrevButton) {
        this.upsellPrevButton.disabled = !hasOverflow || isAtStart;
      }

      if (this.upsellNextButton) {
        this.upsellNextButton.disabled = !hasOverflow || isAtEnd;
      }

      if (this.upsellNav) {
        this.upsellNav.classList.remove("is-hidden");
      }
    }

    updateToolUpsellState(selectedCode) {
      if (!this.toolUpsellContent) return;
      if (!this.toolUpsellPanels.length) {
        this.toolUpsellContent.setAttribute("hidden", "hidden");
        return;
      }

      var normalizedCode = this.normalizeGuideCode(selectedCode || "");
      var hasMatch = false;

      this.toolUpsellPanels.forEach((panel) => {
        var panelCode = this.normalizeGuideCode(panel.getAttribute("data-size-guide-code"));
        var isActive = panelCode === normalizedCode;
        if (isActive) {
          panel.removeAttribute("hidden");
          hasMatch = true;
        } else {
          panel.setAttribute("hidden", "hidden");
        }
      });

      if (hasMatch) {
        this.toolUpsellContent.removeAttribute("hidden");
      } else {
        this.toolUpsellContent.setAttribute("hidden", "hidden");
      }
    }

    handleScroll() {
      if (this.tickingStickyShadow) return;
      this.tickingStickyShadow = true;

      window.requestAnimationFrame(() => {
        this.tickingStickyShadow = false;
        this.updateStickyShadowState();
      });
    }

    updateStickyShadowState() {
      if (!this.tabsShell) return;

      var computedTop = window.getComputedStyle(this.tabsShell).top;
      var stickyTop = parseFloat(computedTop || "0");
      if (!Number.isFinite(stickyTop)) stickyTop = 0;

      var shellRect = this.tabsShell.getBoundingClientRect();
      var isStuck = shellRect.top <= stickyTop + 0.5;
      var wasStuck = this.tabsShell.classList.contains("is-stuck");
      this.tabsShell.classList.toggle("is-stuck", isStuck);

      if (wasStuck !== isStuck) {
        this.updateArrowState();
      }
    }

    handleResize() {
      this.measureLayout();
    }

    activateTab(tab, options) {
      if (!tab) return;

      var config = options || {};
      var targetId = tab.getAttribute("data-size-guide-target") || tab.getAttribute("aria-controls");
      if (!targetId) return;

      var panel = this.root.querySelector("#" + CSS.escape(targetId));
      if (!panel) return;

      var selectedCode = this.normalizeGuideCode(tab.getAttribute("data-size-guide-code") || panel.getAttribute("data-size-guide-code") || "");
      this.activeCode = selectedCode;

      this.tabs.forEach((currentTab) => {
        var isActive = currentTab === tab;
        currentTab.classList.toggle("is-active", isActive);
        currentTab.setAttribute("aria-selected", isActive ? "true" : "false");
        currentTab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.panels.forEach((currentPanel) => {
        if (currentPanel === panel) {
          currentPanel.removeAttribute("hidden");
        } else {
          currentPanel.setAttribute("hidden", "hidden");
        }
      });

      var hasUpsellMatch = false;
      var activeUpsellPanel = null;
      this.upsellPanels.forEach((upsellPanel) => {
        var panelCode = this.normalizeGuideCode(upsellPanel.getAttribute("data-size-guide-code"));
        if (panelCode === selectedCode) {
          upsellPanel.removeAttribute("hidden");
          hasUpsellMatch = true;
          activeUpsellPanel = upsellPanel;
        } else {
          upsellPanel.setAttribute("hidden", "hidden");
        }
      });

      if (!hasUpsellMatch && this.upsellPanels.length) {
        this.upsellPanels[0].removeAttribute("hidden");
        activeUpsellPanel = this.upsellPanels[0];
      }
      this.updateUpsellHeading(activeUpsellPanel);
      this.updateUpsellArrowState();
      this.updateToolUpsellState(selectedCode);

      if (config.ensureVisible) {
        this.ensureTabVisible(tab);
      }

      if (config.setFocus) {
        tab.focus({ preventScroll: true });
      }

      if (config.updateHistory) {
        this.updateGuideQuery(selectedCode);
      }

      this.updateArrowState();
    }

    updateUpsellHeading(activePanel) {
      if (!this.upsellTitleTarget) return;

      var nextTitle = "";
      if (activePanel) {
        nextTitle = (activePanel.getAttribute("data-size-guide-upsell-title") || "").trim();
      }
      if (!nextTitle) {
        nextTitle = (this.upsellTitleTarget.getAttribute("data-default-upsell-title") || "").trim();
      }

      if (nextTitle) {
        this.upsellTitleTarget.textContent = nextTitle;
      }
    }

    normalizeGuideCode(value) {
      if (!value) return "";
      return value
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    }

    getTabByCode(code) {
      if (!code) return null;
      var normalized = this.normalizeGuideCode(code);
      return this.tabs.find((tab) => {
        return this.normalizeGuideCode(tab.getAttribute("data-size-guide-code")) === normalized;
      }) || null;
    }

    getInitialGuideCode() {
      var queryCode = "";
      try {
        queryCode = this.normalizeGuideCode(new URLSearchParams(window.location.search).get("guide"));
      } catch (error) {
        queryCode = "";
      }

      if (queryCode) return queryCode;

      var hash = (window.location.hash || "").replace("#", "");
      if (!hash) return "";

      var hashCode = hash
        .replace(/^size-guide-/, "")
        .replace(/-panel$/, "");
      return this.normalizeGuideCode(hashCode);
    }

    updateGuideQuery(code) {
      if (!window.history || !window.history.replaceState) return;

      try {
        var url = new URL(window.location.href);
        if (code) {
          url.searchParams.set("guide", code);
        } else {
          url.searchParams.delete("guide");
        }
        window.history.replaceState({}, "", url.toString());
      } catch (error) {
        var params = new URLSearchParams(window.location.search);
        if (code) {
          params.set("guide", code);
        } else {
          params.delete("guide");
        }
        var queryString = params.toString();
        var nextUrl = window.location.pathname + (queryString ? "?" + queryString : "") + (window.location.hash || "");
        window.history.replaceState({}, "", nextUrl);
      }
    }
  }

  function supportsCssEscape() {
    return typeof window.CSS !== "undefined" && typeof window.CSS.escape === "function";
  }

  if (!supportsCssEscape()) {
    window.CSS = window.CSS || {};
    window.CSS.escape = function (value) {
      return value.replace(/([^\w-])/g, "\\$1");
    };
  }

  function initSizeGuidePages() {
    var roots = document.querySelectorAll("[data-size-guide-page]");
    if (!roots.length) return;

    roots.forEach((root) => {
      if (root.dataset.sizeGuidePageInitialized === "true") return;
      root.dataset.sizeGuidePageInitialized = "true";

      var page = new SizeGuidePage(root);
      page.init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSizeGuidePages);
  } else {
    initSizeGuidePages();
  }
})();
